import { createClient } from '@/lib/supabase/client';

export type CmsLang = 'en' | 'fr' | 'ar';

export interface CmsSectionContent {
  [key: string]: string;
}

export interface CmsSection {
  section_key: string;
  section_label: string;
  status: 'draft' | 'published';
  content_en: CmsSectionContent;
  content_fr: CmsSectionContent;
  content_ar: CmsSectionContent;
}

export interface CmsTestimonial {
  id: string;
  name: string;
  job_title: string;
  photo_url: string;
  rating: number;
  testimonial_en: string;
  testimonial_fr: string;
  testimonial_ar: string;
  featured: boolean;
  sort_order: number;
}

export interface CmsFaqItem {
  id: string;
  question_en: string;
  answer_en: string;
  question_fr: string;
  answer_fr: string;
  question_ar: string;
  answer_ar: string;
  category: string;
  sort_order: number;
}

/**
 * Fetch a single published section's content for a given language.
 * Falls back to English if the requested language content is empty.
 */
export async function getCmsSectionContent(
  sectionKey: string,
  lang: CmsLang = 'en'
): Promise<CmsSectionContent | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('cms_content_sections')
    .select('content_en, content_fr, content_ar, status')
    .eq('section_key', sectionKey)
    .eq('status', 'published')
    .single();

  if (error || !data) return null;

  const contentKey = `content_${lang}` as keyof typeof data;
  const content = data[contentKey] as CmsSectionContent;
  const fallback = data.content_en as CmsSectionContent;

  // Merge: use lang content, fall back to EN for missing keys
  if (!content || Object.keys(content).length === 0) return fallback;
  const merged: CmsSectionContent = { ...fallback };
  Object.keys(content).forEach(k => {
    if (content[k]) merged[k] = content[k];
  });
  return merged;
}

/**
 * Fetch all published sections as a map: section_key → content
 */
export async function getAllCmsContent(lang: CmsLang = 'en'): Promise<Record<string, CmsSectionContent>> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('cms_content_sections')
    .select('section_key, content_en, content_fr, content_ar')
    .eq('status', 'published')
    .order('sort_order');

  if (error || !data) return {};

  const result: Record<string, CmsSectionContent> = {};
  data.forEach((row: CmsSection & { section_key: string }) => {
    const contentKey = `content_${lang}` as keyof typeof row;
    const content = row[contentKey] as CmsSectionContent;
    const fallback = row.content_en as CmsSectionContent;
    if (!content || Object.keys(content).length === 0) {
      result[row.section_key] = fallback;
    } else {
      const merged: CmsSectionContent = { ...fallback };
      Object.keys(content).forEach(k => { if (content[k]) merged[k] = content[k]; });
      result[row.section_key] = merged;
    }
  });
  return result;
}

/**
 * Fetch published testimonials for a given language
 */
export async function getCmsTestimonials(lang: CmsLang = 'en'): Promise<CmsTestimonial[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('cms_testimonials')
    .select('*')
    .eq('status', 'published')
    .order('sort_order');

  if (error || !data) return [];
  return data as CmsTestimonial[];
}

/**
 * Fetch published FAQ items for a given language
 */
export async function getCmsFaqItems(lang: CmsLang = 'en'): Promise<CmsFaqItem[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('cms_faq_items')
    .select('*')
    .eq('status', 'published')
    .order('sort_order');

  if (error || !data) return [];
  return data as CmsFaqItem[];
}

/**
 * Helper: get the correct language field from a testimonial
 */
export function getTestimonialText(item: CmsTestimonial, lang: CmsLang): string {
  if (lang === 'fr' && item.testimonial_fr) return item.testimonial_fr;
  if (lang === 'ar' && item.testimonial_ar) return item.testimonial_ar;
  return item.testimonial_en || '';
}

/**
 * Helper: get the correct language Q&A from a FAQ item
 */
export function getFaqText(item: CmsFaqItem, lang: CmsLang): { question: string; answer: string } {
  if (lang === 'fr') return { question: item.question_fr || item.question_en, answer: item.answer_fr || item.answer_en };
  if (lang === 'ar') return { question: item.question_ar || item.question_en, answer: item.answer_ar || item.answer_en };
  return { question: item.question_en, answer: item.answer_en };
}
